import { buildSeries } from './buildSeries';
import { buildTones } from './buildTones';
import type { Theme } from './tokens';

/**
 * Slate and ink. A mist ground, white cards and one blue working accent; the
 * grays carry a blue hue so nothing reads warm. The brand's cherry is kept out
 * of the working palette on purpose — it marks the lockup and nothing else,
 * so a red on screen always means danger.
 */
const light: Theme = {
  id: 'light',
  name: 'Light',
  description: 'Mist ground, white cards, ink-blue accent.',
  mode: 'light',
  chromeColor: '#ffffff',
  radius: '0.625rem',
  core: {
    background: 'oklch(0.966 0.005 255)',
    foreground: 'oklch(0.27 0.028 265)',
    card: 'oklch(1 0 0)',
    cardForeground: 'oklch(0.27 0.028 265)',
    popover: 'oklch(1 0 0)',
    popoverForeground: 'oklch(0.27 0.028 265)',
    primary: 'oklch(0.48 0.16 262)',
    primaryForeground: 'oklch(0.99 0 0)',
    secondary: 'oklch(0.935 0.010 255)',
    secondaryForeground: 'oklch(0.27 0.028 265)',
    muted: 'oklch(0.955 0.006 255)',
    mutedForeground: 'oklch(0.53 0.028 262)',
    accent: 'oklch(0.945 0.010 255)',
    accentForeground: 'oklch(0.27 0.028 265)',
    destructive: 'oklch(0.55 0.20 20)',
    destructiveForeground: 'oklch(0.99 0 0)',
    border: 'oklch(0.905 0.010 255)',
    input: 'oklch(0.905 0.010 255)',
    ring: 'oklch(0.48 0.16 262)',
    // The work column sits on the page ground with no fill of its own; these
    // only dress the small-screen navigation sheet.
    sidebar: 'oklch(0.966 0.005 255)',
    sidebarForeground: 'oklch(0.27 0.028 265)',
    sidebarPrimary: 'oklch(0.48 0.16 262)',
    sidebarPrimaryForeground: 'oklch(0.99 0 0)',
    sidebarAccent: 'oklch(1 0 0)',
    sidebarAccentForeground: 'oklch(0.27 0.028 265)',
    sidebarBorder: 'oklch(0.905 0.010 255)',
    sidebarRing: 'oklch(0.48 0.16 262)',
    brandCherry: 'oklch(0.55 0.20 20)',
    brandStem: 'oklch(0.27 0.028 265)',
    chart: [
      'oklch(0.48 0.16 262)',
      'oklch(0.56 0.12 160)',
      'oklch(0.68 0.15 60)',
      'oklch(0.55 0.20 20)',
      'oklch(0.55 0.14 300)',
    ],
  },
  tones: buildTones({ mode: 'light', neutralHue: 255, neutralChroma: 0.012 }),
  series: buildSeries({ mode: 'light' }),
};

/**
 * Dark keeps the same figure/ground relationship: cards a step above the
 * ground, the work column on the ground, and the blue lifted so it still
 * reads as the accent rather than sinking into slate.
 */
const dark: Theme = {
  id: 'dark',
  name: 'Dark',
  description: 'Slate surfaces with the same blue accent.',
  mode: 'dark',
  chromeColor: '#1b2130',
  radius: '0.625rem',
  core: {
    background: 'oklch(0.225 0.020 262)',
    foreground: 'oklch(0.92 0.010 255)',
    card: 'oklch(0.27 0.022 262)',
    cardForeground: 'oklch(0.92 0.010 255)',
    popover: 'oklch(0.29 0.024 262)',
    popoverForeground: 'oklch(0.94 0.010 255)',
    primary: 'oklch(0.74 0.12 258)',
    primaryForeground: 'oklch(0.20 0.030 262)',
    secondary: 'oklch(0.33 0.024 262)',
    secondaryForeground: 'oklch(0.92 0.010 255)',
    muted: 'oklch(0.305 0.022 262)',
    mutedForeground: 'oklch(0.71 0.022 258)',
    accent: 'oklch(0.33 0.026 262)',
    accentForeground: 'oklch(0.94 0.010 255)',
    destructive: 'oklch(0.66 0.19 22)',
    destructiveForeground: 'oklch(0.18 0.030 22)',
    border: 'oklch(0.355 0.022 262)',
    input: 'oklch(0.355 0.022 262)',
    ring: 'oklch(0.74 0.12 258)',
    sidebar: 'oklch(0.225 0.020 262)',
    sidebarForeground: 'oklch(0.92 0.010 255)',
    sidebarPrimary: 'oklch(0.74 0.12 258)',
    sidebarPrimaryForeground: 'oklch(0.20 0.030 262)',
    sidebarAccent: 'oklch(0.27 0.022 262)',
    sidebarAccentForeground: 'oklch(0.94 0.010 255)',
    sidebarBorder: 'oklch(0.355 0.022 262)',
    sidebarRing: 'oklch(0.74 0.12 258)',
    brandCherry: 'oklch(0.72 0.17 18)',
    brandStem: 'oklch(0.92 0.010 255)',
    chart: [
      'oklch(0.74 0.12 258)',
      'oklch(0.72 0.12 160)',
      'oklch(0.80 0.13 70)',
      'oklch(0.72 0.17 18)',
      'oklch(0.72 0.12 300)',
    ],
  },
  // groundL sits between the background and the card, so chip washes clear the
  // card they sit on without floating off it.
  tones: buildTones({ mode: 'dark', groundL: 0.25, neutralHue: 258, neutralChroma: 0.02 }),
  series: buildSeries({ mode: 'dark', groundL: 0.25 }),
};

export const THEMES: Theme[] = [light, dark];

export const DEFAULT_THEME_ID = light.id;

export function getTheme(id: string | null | undefined): Theme {
  return THEMES.find((t) => t.id === id) ?? light;
}
